import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { BottomPanel } from '@euler/components/BottomPanel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ReactNode, memo, useState } from 'react';
import { View } from 'react-native';

/**
 * A ⋯ menu: a caption over its rows, as a sheet. `children` gets `run`,
 * which closes the menu and runs the row's action once the sheet is gone,
 * so an action that opens a panel of its own never stacks two modals.
 */
export const BiMenuSheet = memo(
  ({
    visible,
    title,
    onClose,
    children,
  }: {
    visible: boolean;
    title: string;
    onClose: () => void;
    children: (run: (action: () => void) => () => void) => ReactNode;
  }) => {
    const { rpx } = useResponsiveContext();
    const [pending, setPending] = useState<(() => void) | null>(null);
    const run = (action: () => void) => () => {
      setPending(() => action);
      onClose();
    };
    return (
      <BottomPanel
        visible={visible}
        onClose={onClose}
        fitContent
        onExitComplete={() => {
          setPending(null);
          pending?.();
        }}
      >
        <View
          style={{ padding: rpx(20), gap: rpx(4) }}
          accessibilityRole="menu"
        >
          <Typography.Text
            medium
            size={rpx(13)}
            color={aiColors.mutedForeground}
            numberOfLines={1}
          >
            {title}
          </Typography.Text>
          {children(run)}
        </View>
      </BottomPanel>
    );
  },
);
